/**
 * WebSocket client for the admin realtime channel.
 *
 * The server protocol is unchanged from the Django admin: connect to
 * ws/admin/conferences/, send `{type: 'subscribe', view: 'list'|'detail'|'dashboard'}`,
 * then receive typed messages (see backend/services/conference_admin_realtime.py).
 *
 * One socket is shared across the app. Views register subscriptions and message
 * handlers; the manager re-sends every active subscription after a reconnect so a
 * dropped connection self-heals without the views knowing.
 */

export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface SocketMessage {
  type: string;
  [key: string]: unknown;
}

export type MessageHandler = (message: SocketMessage) => void;
export type StatusHandler = (status: SocketStatus) => void;

export type Subscription =
  | { view: 'list'; query: string }
  | { view: 'dashboard'; query: string }
  | { view: 'detail'; conference_pk: number };

function socketUrl(path: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}`;
}

/** Access denied by the consumer; retrying will not help. */
const CLOSE_FORBIDDEN = 4403;
const PING_INTERVAL_MS = 25_000;
const MAX_BACKOFF_MS = 20_000;

export class AdminSocket {
  private socket: WebSocket | null = null;
  private status: SocketStatus = 'closed';
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private forbidden = false;

  private readonly messageHandlers = new Set<MessageHandler>();
  private readonly statusHandlers = new Set<StatusHandler>();
  /**
   * Keyed so re-subscribing the same view replaces rather than duplicates.
   *
   * An owner token is kept with the subscription. React can briefly mount a
   * replacement effect before an older effect is cleaned up; without the token
   * that stale cleanup could remove the newer subscription and close a live
   * socket underneath it.
   */
  private readonly subscriptions = new Map<string, { owner: symbol; value: Subscription }>();

  constructor(private readonly path = '/ws/admin/conferences/') {}

  getStatus(): SocketStatus {
    return this.status;
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    handler(this.status);
    return () => this.statusHandlers.delete(handler);
  }

  /** Adds a subscription and (re)connects if needed. Returns an unsubscribe fn. */
  subscribe(subscription: Subscription): () => void {
    const key = subscriptionKey(subscription);
    const owner = Symbol(key);
    this.subscriptions.set(key, { owner, value: subscription });
    this.connect();
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.send({ type: 'subscribe', ...subscription });
    }
    return () => {
      const current = this.subscriptions.get(key);
      // A newer mount now owns this view. Its subscription must survive the
      // delayed cleanup from the older mount.
      if (!current || current.owner !== owner) return;
      this.subscriptions.delete(key);
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.send({ type: 'unsubscribe', ...subscription });
      }
      if (this.subscriptions.size === 0) this.close();
    };
  }

  private connect(): void {
    if (this.forbidden) return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const socket = new WebSocket(socketUrl(this.path));
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) {
        socket.close(1000, 'superseded connection');
        return;
      }
      this.attempt = 0;
      this.setStatus('open');
      // Replay every live subscription: after a reconnect the server has no
      // memory of what this client was watching.
      for (const { value: subscription } of this.subscriptions.values()) {
        this.send({ type: 'subscribe', ...subscription });
      }
      this.startPing();
    };

    socket.onmessage = (event) => {
      // A socket that was replaced while it was closing must not keep message
      // handlers, parsed payloads, or component state alive.
      if (this.socket !== socket) return;
      let payload: SocketMessage;
      try {
        payload = JSON.parse(event.data as string) as SocketMessage;
      } catch {
        return;
      }
      if (payload.type === 'pong') return;
      for (const handler of this.messageHandlers) {
        try {
          handler(payload);
        } catch (error) {
          // One bad handler must not stop the others from seeing the message.
          console.error('Admin socket handler failed', error);
        }
      }
    };

    socket.onclose = (event) => {
      // Closing an older socket is expected after a quick route change. Only
      // the connection currently owned by the manager can affect timers,
      // status, or reconnect policy. This is what prevents orphan sockets from
      // accumulating and continuing to receive dashboard broadcasts.
      if (this.socket !== socket) return;
      this.stopPing();
      this.socket = null;
      if (event.code === CLOSE_FORBIDDEN) {
        this.forbidden = true;
        this.setStatus('closed');
        return;
      }
      if (this.subscriptions.size === 0) {
        this.setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose always follows, so reconnection is handled there.
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    this.setStatus('reconnecting');
    // Exponential backoff with jitter, so a server restart does not get a
    // synchronised stampede from every open admin tab.
    const base = Math.min(1000 * 2 ** this.attempt, MAX_BACKOFF_MS);
    const delay = base / 2 + Math.random() * (base / 2);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ type: 'ping', timestamp: Date.now() });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private send(payload: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(payload));
  }

  private close(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopPing();
    this.socket?.close(1000, 'no subscribers');
    this.socket = null;
    this.attempt = 0;
    this.setStatus('closed');
  }

  private setStatus(status: SocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const handler of this.statusHandlers) handler(status);
  }
}

function subscriptionKey(subscription: Subscription): string {
  return subscription.view === 'detail'
    ? `detail:${subscription.conference_pk}`
    : `${subscription.view}`;
}

export const adminSocket = new AdminSocket();

/**
 * Binary audio socket for the whisper dock. Separate from the JSON channel
 * because it carries raw PCM frames and has its own lifecycle per conference.
 */
export function openAudioSocket(conferencePk: number): WebSocket {
  return new WebSocket(socketUrl(`/ws/admin/conferences/${conferencePk}/audio/`));
}

/** Voice-capture socket used by the memory feedback recorder. */
export function openMemoryTranscribeSocket(scope: number | 'inbox'): WebSocket {
  return new WebSocket(socketUrl(`/ws/admin/memory-feedback/${scope}/transcribe/`));
}
